import { Component, EventEmitter, Input, Output } from '@angular/core';

const CLAIM_STATUS_CHIP: Record<string, { label: string; bg: string; fg: string }> = {
  draft:                { label: 'Draft',                 bg: '#E9ECF4',   fg: '#60698B' },
  submitted:            { label: 'Submitted',             bg: '#A653E21A', fg: '#7A2FB8' },
  pm_review:            { label: 'PM Review',             bg: '#A653E21A', fg: '#7A2FB8' },
  sent_back:            { label: 'Sent back',             bg: '#E0202033', fg: '#E02020' },
  sent_to_contractor:   { label: 'Sent to contractor',    bg: '#A653E21A', fg: '#7A2FB8' },
  contractor_notified:  { label: 'Sent to contractor',    bg: '#A653E21A', fg: '#7A2FB8' },
  responded:            { label: 'Contractor responded',  bg: '#A653E21A', fg: '#7A2FB8' },
  in_progress:          { label: 'In Progress',           bg: '#1036D61A', fg: '#1036D6' },
  approved:             { label: 'Approved',              bg: '#36AB1933', fg: '#186F03' },
  verified:             { label: 'Verified',              bg: '#36AB1933', fg: '#186F03' },
  remediation_complete: { label: 'Remediation complete',  bg: '#36AB1933', fg: '#186F03' },
  closed:               { label: 'Closed',                bg: '#E9ECF4',   fg: '#60698B' },
  rejected:             { label: 'Rejected',              bg: '#E0202033', fg: '#E02020' },
  disputed:             { label: 'Disputed',              bg: '#E0202033', fg: '#E02020' },
};

// Punch list items share this popup but use issue-workflow wording
// (Forwarded / Responded). Warranty claims keep their own wording.
const PUNCHLIST_STATUS_LABEL_OVERRIDES: Record<string, string> = {
  sent_to_contractor: 'Forwarded',
  responded: 'Responded',
};

const CLAIM_SEVERITY_CHIP: Record<string, { label: string; bg: string; fg: string }> = {
  low:      { label: 'Low',      bg: '#1036D61A', fg: '#1036D6' },
  medium:   { label: 'Medium',   bg: '#A653E21A', fg: '#7A2FB8' },
  high:     { label: 'High',     bg: '#FF6B0033', fg: '#CC5500' },
  critical: { label: 'Critical', bg: '#E0202033', fg: '#E02020' },
};

@Component({
  selector: 'app-claim-map-popup',
  templateUrl: './claim-map-popup.component.html',
  styleUrls: ['./claim-map-popup.component.scss'],
  standalone: false,
})
export class ClaimMapPopupComponent {
  @Input() data: any = {};
  @Input() claims: any[] | null = null;
  @Output() claimSelected = new EventEmitter<any>();
  @Output() seeMore = new EventEmitter<any>();

  get isGrouped(): boolean {
    return Array.isArray(this.claims) && this.claims.length > 1;
  }

  get groupCount(): number {
    return this.claims?.length || 0;
  }

  get isPunchlist(): boolean {
    const sample = this.claims?.[0] || this.data || {};
    return sample?._kind === 'punchlist';
  }

  get titleSingular(): string {
    return this.isPunchlist ? 'PUNCH ITEM' : 'WARRANTY CLAIM';
  }

  get titlePlural(): string {
    return this.isPunchlist ? 'PUNCH ITEMS' : 'WARRANTY CLAIMS';
  }

  statusFor(claim: any) {
    const s = (claim?.status || claim?.claim_status || 'draft') as string;
    const chip = CLAIM_STATUS_CHIP[s] || { label: s, bg: '#E9ECF4', fg: '#60698B' };
    const override = claim?._kind === 'punchlist' ? PUNCHLIST_STATUS_LABEL_OVERRIDES[s] : undefined;
    return override ? { ...chip, label: override } : chip;
  }

  severityFor(claim: any) {
    const sv = (claim?.severity || '') as string;
    return CLAIM_SEVERITY_CHIP[sv] || null;
  }

  numberFor(claim: any): string {
    return claim?.claim_number || claim?.number || '';
  }

  titleFor(claim: any): string {
    return claim?.title || '';
  }

  descriptionFor(claim: any): string {
    return claim?.description || '';
  }

  dateFor(claim: any): string | Date | null {
    return claim?.created_at || claim?.report_date || claim?.need_by_date || null;
  }

  thumbsFor(claim: any): any[] {
    const pics = (claim?.pictures || []) as any[];
    return pics.slice(0, 2);
  }

  get status() { return this.statusFor(this.data); }
  get severity() { return this.severityFor(this.data); }
  get claimNumber(): string { return this.numberFor(this.data); }
  get title(): string { return this.titleFor(this.data); }
  get description(): string { return this.descriptionFor(this.data); }
  get date(): string | Date | null { return this.dateFor(this.data); }
  get thumbs(): any[] { return this.thumbsFor(this.data); }

  onSelectClaim(claim: any): void {
    this.claimSelected.emit(claim);
  }

  onSeeMore(): void {
    this.seeMore.emit(this.data);
    this.claimSelected.emit(this.data);
  }
}
